import React, { useMemo } from "react";
import { Alert, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { filterHostelsForStudentGender, studentGenderToHostelGender } from "../../utils/hostelGenderUtils";
import { roomsForHostel } from "../../utils/hostelEnrollmentOptions";

/**
 * Hostel then room — used when enrolling boarding students.
 */
export default function HostelRoomPickerFields({
    hostels = [],
    hostelId = "",
    hostelRoomId = "",
    bedLabel = "",
    studentGender = "",
    onHostelChange,
    onRoomChange,
    onBedLabelChange,
    boardingMode = "Mixed",
    roomRequired = true,
}) {
    const eligibleHostels = useMemo(
        () => filterHostelsForStudentGender(hostels, studentGender),
        [hostels, studentGender],
    );
    const rooms = useMemo(() => roomsForHostel(eligibleHostels, hostelId), [eligibleHostels, hostelId]);
    const noHostels = eligibleHostels.length === 0;
    const genderRequired = studentGenderToHostelGender(studentGender) == null;

    return (
        <>
            {genderRequired ? (
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Select the student&apos;s gender (Male or Female) above before choosing a hostel.
                    </Alert>
                </Grid>
            ) : null}
            <Grid item xs={12} sm={6}>
                <FormControl
                    fullWidth
                    required={roomRequired && boardingMode === "BoardingOnly"}
                    disabled={genderRequired}
                >
                    <InputLabel id="hostel-label">Hostel</InputLabel>
                    <Select
                        labelId="hostel-label"
                        label="Hostel"
                        value={hostelId}
                        onChange={(e) => onHostelChange?.(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>
                                {noHostels
                                    ? genderRequired
                                        ? "Set student gender first"
                                        : `No ${studentGenderToHostelGender(studentGender)?.toLowerCase() || ""} hostels — add under Hostels & boarding`
                                    : "Select hostel"}
                            </em>
                        </MenuItem>
                        {eligibleHostels.map((h) => (
                            <MenuItem key={h.id} value={h.id}>
                                {h.name}
                                {h.gender ? ` (${h.gender === "Boys" ? "Boys only" : "Girls only"})` : ""}
                                {(h.rooms || []).length
                                    ? ` — ${(h.rooms || []).filter((r) => r.vacant > 0).length} room(s) with space`
                                    : " (no rooms)"}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl
                    fullWidth
                    required={roomRequired}
                    disabled={!hostelId || rooms.length === 0}
                >
                    <InputLabel id="hostel-room-label">Room</InputLabel>
                    <Select
                        labelId="hostel-room-label"
                        label="Room"
                        value={hostelRoomId}
                        onChange={(e) => onRoomChange?.(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>
                                {!hostelId
                                    ? "Select a hostel first"
                                    : rooms.length
                                      ? "Select room"
                                      : "No rooms in this hostel"}
                            </em>
                        </MenuItem>
                        {rooms.map((room) => (
                            <MenuItem key={room.id} value={room.id} disabled={room.vacant <= 0}>
                                {room.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label="Bed label (optional)"
                    value={bedLabel}
                    onChange={(e) => onBedLabelChange?.(e.target.value)}
                    placeholder="e.g. Bed 2"
                />
            </Grid>
        </>
    );
}
